import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import cx from "classnames";
import { useEffect, useRef, useState } from "react";

import { useFeatureFlag } from "services/Flags";

import {
  getAdonisRecommendationsBySession,
  getAdonisStarterQuestions,
  warmAdonisCache,
} from "../adonis.helpers";
import { AdonisSession } from "../adonis.types";
import { useAdonisChat } from "../AdonisChatProvider/AdonisChatProvider";
import ChatEmptyState from "../components/ChatEmptyState/ChatEmptyState";
import { EngineHttpChat, EngineHttpChatHandle } from "../engine/EngineHttpChat";
import { AdonisRecommendation } from "../IAdonisRecommendation";
import {
  AssistantContentBlock,
  Message,
} from "../streaming/streaming-message-types";

// Input and streaming handler are now managed by EngineHttpChat
import {
  ToolCallStatusHandle,
  ToolCallStatusProvider,
} from "./AdonistToolCalResponseProvider";

const ERROR_MESSAGE = "I'm sorry, please try to submit your message again";

// Helper function to convert AdonisRecommendation to Message array
// Each AdonisRecommendation contains BOTH userQuery and adonisResponse,
// so we need to convert each record to TWO messages (user + assistant)
const convertAdonisToMessages = (rec: AdonisRecommendation): Message[] => {
  const messages: Message[] = [];

  // Add user message if userQuery exists (UserMessage content must be string)
  if (rec.userQuery) {
    try {
      // anthorpic returns tool_result blocks as JSON array
      const parsed = JSON.parse(rec.userQuery);
      if (Array.isArray(parsed)) {
        const toolResults = parsed.filter(
          (c) => c.type === "tool_result"
        ) as Array<{
          type: "tool_result";
          tool_use_id: string;
          content: string;
        }>;

        if (toolResults.length > 0) {
          messages.push({
            role: "tool",
            content: toolResults,
            displayState: { kind: "displayed" },
          });
        }
      }
    } catch {
      // we store text content blocks as strings, not as JSON
      // this should be addressed in a future PR in the backend
      const userText = rec.userQuery;
      if (userText.trim().length > 0) {
        messages.push({
          role: "user",
          content: userText,
          displayState: { kind: "displayed" },
        });
      }
    }
  }

  // Add assistant message if adonisResponse exists (no streaming for historical)
  if (rec.adonisResponse) {
    // Parse the response as MessageContent and extract display text
    let parsedContent: AssistantContentBlock[];
    try {
      // Try to parse as JSON first (new format)
      parsedContent = JSON.parse(rec.adonisResponse);
    } catch {
      // Fall back to string format (legacy)
      parsedContent = [{ type: "text", text: rec.adonisResponse }];
    }

    messages.push({
      role: "assistant",
      content: parsedContent,
      displayState: {
        kind: "displayed",
      },
    });
  }

  return messages;
};

export interface AdonisChatText {
  header: string;
  new_chat: string;
  chose_question_or_chat: string;
  chat_box: string;
}

type AdonisChatComponentProps = {
  session: AdonisSession;
  chatText?: AdonisChatText;
  isOpen: boolean;
  pendingMessage?: string | null;
};

export const AdonisChatComponent = ({
  session,
  chatText,
  isOpen,
  pendingMessage,
}: AdonisChatComponentProps) => {
  const queryClient = useQueryClient();
  const [recommendationInput, setRecommendationInput] = useState<string>("");
  const [keyboardIsOpen, setKeyboardIsOpen] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [isWarmupComplete, setIsWarmupComplete] = useState(false);
  const [isWarmingUp, setIsWarmingUp] = useState(false);
  const [warmupError, setWarmupError] = useState(false);
  const [pendingMessageSubmitted, setPendingMessageSubmitted] = useState(false);

  const [warmedUpSessionIds, setWarmedUpSessionIds] = useState<Set<string>>(
    new Set()
  );

  // Engine integration ref
  const engineRef = useRef<EngineHttpChatHandle>(null);

  const { setHasMessages, clearPendingMessage } = useAdonisChat();
  // Legacy streaming callbacks removed; engine handles UI state
  const adonisChatText = useFeatureFlag("adonisChatText");

  const defaultChatText = {
    header: "I'm Adonis, your AI treatment advisor.",
    new_chat: "NEW CHAT STARTED",
    chose_question_or_chat: "CHOOSE A QUESTION OR ASK BELOW TO START",
    chat_box: "Type a message...",
  };

  const finalChatText = chatText || adonisChatText || defaultChatText;

  // Scrolling is managed inside EngineHttpChat

  useEffect(() => {
    // Only handle keyboard events for native platforms
    // Mobile web will use CSS dynamic viewport units (dvh)
    if (Capacitor.isNativePlatform()) {
      const keyboardWillShowListener = Keyboard.addListener(
        "keyboardWillShow",
        (info) => {
          setKeyboardIsOpen(true);
          setKeyboardHeight(info.keyboardHeight);
        }
      );

      const keyboardWillHideListener = Keyboard.addListener(
        "keyboardWillHide",
        () => {
          setKeyboardIsOpen(false);
          setKeyboardHeight(0);
        }
      );

      return () => {
        keyboardWillShowListener.then((listener) => listener.remove());
        keyboardWillHideListener.then((listener) => listener.remove());
      };
    }
  }, []);

  // Auto-scroll behavior now lives inside EngineHttpChat

  // Clear the warmup tracking so it can warm up again when the session changes
  useEffect(() => {
    if (session?.id) {
      resetChat();
      setWarmedUpSessionIds(new Set());
      setIsWarmupComplete(false);
      setWarmupError(false);
      setPendingMessageSubmitted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id]);

  // Auto-submit pending message when warmup completes
  useEffect(() => {
    if (
      isWarmupComplete &&
      pendingMessage &&
      !pendingMessageSubmitted &&
      session?.id
    ) {
      setPendingMessageSubmitted(true);
      void handleSendRecommendation(pendingMessage).then(() => {
        clearPendingMessage();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWarmupComplete, pendingMessage, pendingMessageSubmitted, session?.id]);

  // Warmup when modal opens AND session is available AND not already warmed up
  useEffect(() => {
    if (isOpen && session?.id && !warmedUpSessionIds.has(session.id)) {
      void performWarmup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, session?.id]);

  const toolContextRef = useRef<ToolCallStatusHandle>(null);

  const { isLoading: isLoadingRecommendations } = useQuery({
    queryKey: ["recommendations-history", session.id],
    queryFn: async () => {
      const recommendationsBySession = await getAdonisRecommendationsBySession(
        session.id
      );
      // Convert AdonisRecommendation[] to Message[] format
      // Each AdonisRecommendation becomes 1-2 Messages (user + assistant)
      const convertedMessages = recommendationsBySession.flatMap(
        convertAdonisToMessages
      );

      engineRef.current?.getEngine().seedHistory(convertedMessages);

      convertedMessages.forEach((message) => {
        if (Array.isArray(message.content)) {
          for (const contentEntry of message.content) {
            if (contentEntry.type === "tool_result") {
              toolContextRef.current?.markToolSuccess(
                contentEntry.tool_use_id,
                {
                  status: "success",
                  response: contentEntry.content,
                }
              );
            }
          }
        }
      });

      return recommendationsBySession;
    },
    staleTime: 60000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const performWarmup = async () => {
    if (!session?.id || isWarmingUp || warmedUpSessionIds.has(session.id))
      return;

    setIsWarmingUp(true);
    setIsWarmupComplete(false);
    setWarmupError(false);

    try {
      const success = await warmAdonisCache(session.id);
      setIsWarmupComplete(success);
      if (success) {
        setWarmedUpSessionIds((prev) => new Set(prev).add(session.id));
      } else {
        console.warn("Cache warmup failed");
        setWarmupError(true);
      }
    } catch (error) {
      console.error("Error during warmup:", error);
      setIsWarmupComplete(false);
      setWarmupError(true);
    } finally {
      setIsWarmingUp(false);
    }
  };

  const resetChat = () => {
    setRecommendationInput("");
    queryClient.invalidateQueries({
      queryKey: ["recommendations-history", session.id],
    });
    queryClient.invalidateQueries({
      queryKey: ["recommendation", session.id],
    });
  };

  // Removed local scrollToBottom; handled by EngineHttpChat

  const { data: starterQuestions } = useQuery({
    queryKey: ["starter-questions"],
    queryFn: async () => {
      return getAdonisStarterQuestions();
    },
  });

  const handleSendRecommendation = async (text?: string) => {
    const currentRecommendation = text || recommendationInput;
    if (!currentRecommendation.trim() || !session || !isWarmupComplete) {
      return;
    }

    setRecommendationInput("");
    try {
      if (Capacitor.isNativePlatform()) {
        Keyboard.hide();
      }
      await engineRef.current
        ?.getEngine()
        .sendUserMessage(currentRecommendation);
      setHasMessages(true);
    } catch (error) {
      console.error("Error during engine send:", error);
    }
  };

  return (
    <ToolCallStatusProvider ref={toolContextRef}>
      <div
        data-testid="adonis-chat-component"
        className={cx(
          "relative flex h-full flex-col rounded-lg bg-gradient-to-b from-white to-[#ECDAC3] shadow-md"
        )}
      >
        <div
          className="
              pointer-events-none
              absolute inset-x-0 top-0
              z-10
              h-16
              bg-gradient-to-b
              from-white to-transparent"
        />
        <EngineHttpChat
          ref={engineRef}
          sessionId={session.id}
          keyboardIsOpen={keyboardIsOpen}
          keyboardHeight={keyboardHeight}
          placeholder={finalChatText.chat_box}
          isWarmupComplete={isWarmupComplete}
          isWarmingUp={isWarmingUp}
          emptyState={
            <ChatEmptyState
              isLoadingRecommendations={isLoadingRecommendations}
              keyboardIsOpen={keyboardIsOpen}
              newChatLabel={finalChatText.new_chat}
              starterQuestions={starterQuestions}
              isWarmupComplete={isWarmupComplete}
              isWarmingUp={isWarmingUp}
              warmupError={warmupError}
              isStreaming={false}
              onSendRecommendation={handleSendRecommendation}
              headerTemplate={finalChatText.header}
              hasPendingMessage={!!pendingMessage}
            />
          }
        />
        <div
          className="
          pointer-events-none
          absolute inset-x-0 bottom-[76px]
          h-20
          bg-gradient-to-t
          from-[#ECDAC3]
          to-transparent
        "
        />
        {/* Input is rendered by EngineHttpChat now */}
      </div>
    </ToolCallStatusProvider>
  );
};

export default AdonisChatComponent;
